/**
 * Notable Models — Epoch AI's curated registry of frontier-class training
 * runs (~1000 entries, refreshed daily). Sourced from /api/frontier/notable-models.
 *
 * Shows: model name, organisation, publication date, parameters, training
 * compute (FLOP), country, domain. Filter by org / year / domain / slice.
 *
 * The curated 17-entry timeline lives separately on /app/model-releases —
 * that's editorial highlights; this page is the canonical industry registry.
 */
import { useEffect, useMemo, useState } from 'react'
import { Database, RefreshCw, Filter, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

interface NotableModel {
  model: string
  organization: string
  publication_date: string | null
  domain: string | null
  task: string | null
  parameters: number | null
  training_compute: number | null
  training_dataset_size: number | null
  country: string | null
  reference: string | null
  notability: string | null
  confidence: string | null
}

const SLICE_LABEL: Record<string, string> = {
  notable:  'Notable (curated)',
  frontier: 'Frontier (>10²³ FLOP)',
}

function formatBig(n: number | null): string {
  if (n == null || n === 0) return '—'
  if (n >= 1e24) return `${(n / 1e24).toFixed(1)}Y`
  if (n >= 1e21) return `${(n / 1e21).toFixed(1)}Z`
  if (n >= 1e18) return `${(n / 1e18).toFixed(1)}E`
  if (n >= 1e15) return `${(n / 1e15).toFixed(1)}P`
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9)  return `${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6)  return `${(n / 1e6).toFixed(1)}M`
  return n.toLocaleString()
}

export default function NotableModelsPage() {
  const [models, setModels] = useState<NotableModel[]>([])
  const [slice, setSlice] = useState<'notable' | 'frontier'>('notable')
  const [orgFilter, setOrgFilter] = useState('')
  const [yearFilter, setYearFilter] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)

  const load = (refresh = false) => {
    setLoading(true)
    api.get(`/api/frontier/notable-models`, { params: { slice, refresh } })
      .then(r => {
        setModels(r.data?.models || [])
        setFetchedAt(r.data?.fetched_at || null)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load(false) }, [slice])

  const orgs = useMemo(
    () => Array.from(new Set(models.map(m => m.organization).filter(Boolean))).sort(),
    [models],
  )
  const years = useMemo(
    () => Array.from(new Set(models.map(m => m.publication_date?.slice(0, 4)).filter(Boolean))).sort().reverse(),
    [models],
  )

  const filtered = useMemo(() => models.filter(m =>
    (!orgFilter  || m.organization === orgFilter) &&
    (!yearFilter || m.publication_date?.startsWith(yearFilter)) &&
    (!search     || `${m.model} ${m.organization} ${m.task || ''}`.toLowerCase().includes(search.toLowerCase()))
  ).sort((a, b) => (b.publication_date || '').localeCompare(a.publication_date || '')),
  [models, orgFilter, yearFilter, search])

  // Per-year count for the sparkline tile
  const perYear = useMemo(() => {
    if (models.length === 0) return []
    const yearMap: Record<string, number> = {}
    for (const m of models) {
      const y = m.publication_date?.slice(0, 4)
      if (y) yearMap[y] = (yearMap[y] || 0) + 1
    }
    return Object.entries(yearMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([t, v]) => ({ t, v }))
  }, [models])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Database size={18} className="text-emerald" /> NOTABLE MODELS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          epoch ai · live registry · {SLICE_LABEL[slice]}
        </span>
        {fetchedAt && (
          <span className="text-[10px] text-text-muted font-mono">
            updated {fetchedAt.slice(0, 16).replace('T', ' ')} UTC
          </span>
        )}
        <button type="button" onClick={() => load(true)} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Force refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <div className="flex items-baseline gap-2">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Total</div>
            {perYear.length >= 2 && (
              <Sparkline points={perYear} tone="emerald" width={60} height={14} className="ml-auto" />
            )}
          </div>
          <div className="text-2xl font-black font-mono mt-1 text-emerald">{models.length.toLocaleString()}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Organisations</div>
          <div className="text-2xl font-black font-mono mt-1 text-blue-light">{orgs.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Years covered</div>
          <div className="text-2xl font-black font-mono mt-1 text-amber-light">{years.length}</div>
        </div>
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-widest text-text-muted">Filtered shown</div>
          <div className="text-2xl font-black font-mono mt-1 text-purple-light">{filtered.length.toLocaleString()}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={slice} onChange={e => setSlice(e.target.value as 'notable' | 'frontier')}
          aria-label="Slice">
          <option value="notable">Notable (~1000)</option>
          <option value="frontier">Frontier (~140)</option>
        </select>
        <select className="input" value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
          aria-label="Filter by organisation">
          <option value="">All organisations</option>
          {orgs.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="input" value={yearFilter} onChange={e => setYearFilter(e.target.value)}
          aria-label="Filter by year">
          <option value="">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <input className="input flex-1 min-w-[200px]" placeholder="Search model name / org / task"
          value={search} onChange={e => setSearch(e.target.value)} aria-label="Search" />
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto max-h-[640px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-raised z-10">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Model</th>
                <th className="text-left px-3 py-2">Organisation</th>
                <th className="text-left px-3 py-2">Domain</th>
                <th className="text-right px-3 py-2">Params</th>
                <th className="text-right px-3 py-2">Compute (FLOP)</th>
                <th className="text-left px-3 py-2">Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.slice(0, 400).map((m, i) => (
                <tr key={`${m.model}-${i}`} className="hover:bg-surface-raised/40 transition-colors">
                  <td className="px-3 py-1.5 font-mono text-text-muted whitespace-nowrap">
                    {m.publication_date || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-semibold text-text-primary">{m.model}</td>
                  <td className="px-3 py-1.5 text-purple-light">{m.organization}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{m.domain || '—'}</td>
                  <td className="px-3 py-1.5 font-mono text-right text-blue-light">{formatBig(m.parameters)}</td>
                  <td className="px-3 py-1.5 font-mono text-right text-amber-light">{formatBig(m.training_compute)}</td>
                  <td className="px-3 py-1.5">
                    {m.reference && (
                      <a href={m.reference} target="_blank" rel="noreferrer"
                        className="text-blue-light hover:underline inline-flex items-center gap-1">
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 400 && (
            <div className="p-3 text-center text-[10px] text-text-muted bg-surface-raised">
              Showing 400 of {filtered.length.toLocaleString()} matches — narrow with filters above.
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Source: <a href="https://epoch.ai/data/notable-ai-models" target="_blank" rel="noreferrer"
          className={clsx('text-blue-light hover:underline')}>Epoch AI Notable Models dataset</a>
        {' · '}refreshed every 24h.
      </p>
    </div>
  )
}
